/* eslint-disable camelcase */
/* eslint-disable class-methods-use-this */
/* eslint-disable react/jsx-filename-extension */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * AETHomePlugin.js
 * Plugin for Home
 */

import React from 'react';
import { Button } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { getSnapshot } from 'mobx-state-tree';
import {
  DashboardCard,
  WelcomeCard,
  ProgressCard,
  CustomCardList,
  AssignmentProgressCardContainer,
  EmptyStateCard,
  GridContainer, GridItem,
  MultiRemediationComponent, TabularContainer, BadgeTemplate, OverviewTemplate, DifficultTopicDetailsContainer
} from '@aet/components';
import Framework from '@greenville/framework';
import env from '../env';
import * as constants from '../constants';
import AssignmentHelperUtils from '../utils/AssignmentHelperUtils';
import CommonUtils from '../utils/CommonUtils';
import { isAssignmentsDueThisWeek, getSortedAssignmentsByChapterOrSectionOrder } from '../../modules/assignableEtext/utils/AssignmentUtils';
import { DashboardCardSkeleton, CardSkeleton, ListSkeleton } from '../components/skeleton/Skeleton';
import LoadingContainer from '../components/LoadingContainer';
import {
  AET_STORE_EVENTS,
  TOPIC_STYLES,
  REPORT_QUESTION_REASONS,
  SHOW_PATTERN_IN_AI_USE,
  TABULAR_SEARCH_SORT
} from '../../modules/assignableEtext/common/Constants';

export default class AETHomePlugin {
  constructor(context) {
    this.context = context; // the InstructorDashboard instance
    this.language = context.props.language;
    this.context.state = {
      ...this.context.state,
      isReviewPopupOpen: false
    };
  }

  /**
   * Triggers the Start Question Issue Report event
   * @eventNumber 987
   * @returns {void}
   */
  handleReviewClick = () => {
    const { match } = this.context.props || {};
    const { assignmentId, difficultTopicId } = match?.params || {};
    const isStudent = this.context.instructorDashboardUtils.isStudent();

    if (!isStudent && assignmentId) {
      const { ASSIGNMENT_QUESTION_DETAIL } = constants.AET_EVENT.LOCATION_IN_APP;

      // Event: 987
      CommonUtils.dispatchGaEvent(
        constants.AET_EVENT.EVENT_CATEGORY.AI_INSERTED_QUESTION,
        constants.EVENT_USER_INTERACTION,
        constants.AET_EVENT.EVENT_ACTION.START_QUESTION_ISSUE_REPORT,
        null,
        {
          ...CommonUtils.aetCustomFields(
            this.context,
            ASSIGNMENT_QUESTION_DETAIL
          ),
          assignment_id: String(assignmentId),
          ...(difficultTopicId != null ? { question_id: String(difficultTopicId) } : {})
        }
      );
    }

    this.context.setState({ isReviewPopupOpen: true });
  };

  handleClosePopup = () => {
    this.context.setState({ isReviewPopupOpen: false });
  };

  /** True when badge topics are a single N/A insight label. */
  isNaInsightBadge = (topics) => (
    Array.isArray(topics)
    && topics.length === 1
    && topics[0] === constants.PERFORMANCE_STATUS.NA
  );
  
  renderHomeTab = () => {
    const {
      assignment,
      assignmentGetStatus,
      classes
    } = this.context.props;
    const assignments = getSnapshot(assignment)?.assignments;
    const isStudent = this.context.instructorDashboardUtils.isStudent();
    const { isAssignmentsFetched } = this.context.state;
    const { isPending, isError } = getSnapshot(assignmentGetStatus);

    if (isPending || (!isAssignmentsFetched && !isError)) {
      return (
        <DashboardCardSkeleton>
          <div
            className={classes.skeletonContainer}
          >
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </DashboardCardSkeleton>
      );
    }

    if (!isAssignmentsFetched && isError) {
      return (
        <EmptyStateCard
          title={this.language.getMessage('aet.assignments.loadingErrorTitle')}
          description={this.language.getMessage('aet.assignments.loadingErrorDescription')}
          iconName="BugIcon"
        />
      );
    }

    const upNextAssignment = this.getUpNextAssignment();

    if (isStudent) {
      return this.renderStudentHomeTab();
    }

    if (upNextAssignment) {
      return this.renderUpNextAssignment(upNextAssignment);
    }

    if (assignments && assignments?.length > 0) {
      return (
        <DashboardCard
          onInit={() => {
            this.context.initPageTracker();
            // Event: 877
            this.context.instructorDashboardUtils.tabChangeEvent();
          }}
        >
          <EmptyStateCard
          title={this.language.getMessage('aet.home.allCaughtUpTitle')}
            icon="BookIcon"
          />
        </DashboardCard>
      );
    }

    return this.renderWelcomeCard();
  };

  /**
   * Handle view details
   * @eventNumber 894
   * @param {*} action
   * @returns {void}
   */
  handleViewDetails = (action) => {
    const { assignmentId } = action?.details || {};
    const { match, location } = this.context.props;
    // Event: 933
    // Event: 894
    CommonUtils.dispatchGaEvent(
      constants.AET_EVENT.EVENT_CATEGORY.DASHBOARD,
      constants.EVENT_USER_INTERACTION,
      constants.AET_EVENT.EVENT_ACTION.OPEN_ASSIGNMENT_DETAILS,
      null,
      this.context.instructorDashboardUtils.buildOpenAssignmentDetailsCustomFields(assignmentId)
    );
    const url = CommonUtils.getContextAwareRoute('ASSIGNMENT_DETAILS', match, { assignmentId });
    const [pathname, query] = url.split('?');

    Framework.redirectTo(
      {
        pathname,
        search: query ? `?${query}` : null,
        state: {
          from: [`${location.pathname}${location.search}`]
        }
      }
    );
  };

  // eslint-disable-next-line arrow-body-style
  renderStudentHomeTab = () => {
    return this.renderNextDueThisWeekCards();
  };

  renderNextDueThisWeekCards = () => {
    const {
      classes, assignment,
      channelRemediationByBook,
      user,
      channelRemediationByBookStatus,
      herobanner
    } = this.context.props;
    const { isScreenResBelow768 } = this.context.state;
    const userSnapshot = getSnapshot(user);
    const { id: studentId } = userSnapshot || {};
    const dueThisWeekAssignments = assignment.getDueThisWeekAssignments();
    const { isPending } = getSnapshot(channelRemediationByBookStatus);
    const isAssignmentDueThisWeek = isAssignmentsDueThisWeek();

    const nextDueThisWeekCardsTitle = isAssignmentDueThisWeek ? this.language.getMessage('aet.home.dueThisWeekTitle') : this.language.getMessage('aet.home.nextAssignmentTitle');

    const dashboardCardProps = dueThisWeekAssignments.length > 0 ? {
      classes: {
        root: `${classes.wrapperBackground}`,
        content: `${classes.progressCardRootAssignmentProgressCard}`
      }
    } : {
      classes: {
        root: classes.wrapperBackground
      }
    };

    const { byChaptersAssets } = getSnapshot(channelRemediationByBook);
    const remediationData = byChaptersAssets;

    return (
      <div className={classes.flexColumn}>
        <DashboardCard
          title={dueThisWeekAssignments.length > 0 ? nextDueThisWeekCardsTitle : this.language.getMessage('aet.home.whatDueThisWeekTitle')}
          {...dashboardCardProps}
          onInit={() => {
            this.context.initPageTracker();
            // Event: 877
            this.context.instructorDashboardUtils.tabChangeEvent();
            // Event: 876
            this.context.instructorDashboardUtils.tiggerSystemNextAssignmentEvent();
          }}
        >
          {dueThisWeekAssignments.length > 0
            ? (
              <GridContainer
                spacing={16}
                columns={{
                  xs: 1, sm: 1, md: 2, mlg: 2, lg: 2, xl: 3, xxl: 4
                }}
              >
                {dueThisWeekAssignments.map((upNextAssignment) => {
                  const studentProgress = assignment.getAssignmentStudentProgress(
                    studentId, upNextAssignment?.assignmentId
                  );
                  const {
                    questionsAssigned
                  } = studentProgress || {};
                  let performance = studentProgress?.performance;

                  if (!studentProgress?.performance) {
                    performance = {
                      ...assignment.getDefaultPerformance()
                    };
                  }

                  const { performanceData, performanceTooltip } = AssignmentHelperUtils.renderPerformanceProgress(
                    performance,
                    studentProgress?.questionsAssigned || 0
                  );
                  const { progressStatus, label } = this.context.instructorDashboardUtils
                    .assignmentDetailsPlugin.getStudentProgress({
                      completedQuestions: studentProgress?.questionsAttempted || 0,
                      totalQuestions: studentProgress?.questionsAssigned || 0,
                      status: studentProgress?.status
                    });
                  const localTimeZoneLabel = this.context.instructorDashboardUtils.getLocalTimeZoneLabel();

                  return (
                    <GridItem key={upNextAssignment.assignmentId} colspan={1}>
                      <div className={classes.assignmentCardWrapper}>
                        <AssignmentProgressCardContainer
                          progressBarButtonTooltip={this.language.getMessage('aet.assignments.performanceProgressBar')}
                          assignmentId={upNextAssignment?.assignmentId}
                          // Event: 873
                          onCardClick={this.context.instructorDashboardUtils.handleAssignmentClick}
                          theme={constants.THEMES.LIGHT}
                          title={upNextAssignment.title}
                          status={{
                            label,
                            id: progressStatus
                          }}
                          sections={{
                            label: this.language.getMessage('aet.home.sectionsLabel'),
                            sections: AssignmentHelperUtils.getAssignmentChaptersNumbers(upNextAssignment.assignmentTags)
                          }}
                          tooltipSegments={performanceTooltip.slice(1)}
                          segments={performanceData}
                          footer={{
                            badge: <FormattedMessage
                              {...this.language.getText('aet.home.questionsCount')}
                              values={{ count: questionsAssigned || 0 }}
                            />,
                            rightText: <FormattedMessage
                              {...this.language.getText('aet.home.dueLabel')}
                              values={{
                                dueDate: localTimeZoneLabel
                                  ? `${CommonUtils.formatLocalDateTime(upNextAssignment.dueDate)} ${localTimeZoneLabel}`
                                  : CommonUtils.formatLocalDateTime(upNextAssignment.dueDate)
                              }}
                            />
                          }}
                          onStatusHover={() => {
                            // Event: 926
                            this.context.instructorDashboardUtils.triggerHoverCompletionLabelEvent(upNextAssignment);
                          }}
                          onProgressBarHover={() => {
                            // Event: 912
                            this.context.instructorDashboardUtils.triggerHoverPerformanceGraphEvent(upNextAssignment);
                          }}
                        />
                      </div>
                    </GridItem>
                  );
                })}
              </GridContainer>
            ) : (
              <EmptyStateCard
                title={this.language.getMessage('aet.home.allCaughtUpTitle')}
                description={this.language.getMessage('aet.home.allCaughtUpDescription')}
                iconName="BookIcon"
              />
            )}
        </DashboardCard>
        <LoadingContainer
          isReadyToRender={!isPending}
          loader={(
            <DashboardCardSkeleton>
              <div className={classes.skeletonContainer}>
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </div>
            </DashboardCardSkeleton>
          )}
        >
          {CommonUtils.shouldShowChannelsRemediation(remediationData)
            ? (
              <DashboardCard
                title={this.language.getMessage('aet.home.suggestedRemediationTitle')}
                subTitle={!isScreenResBelow768 ? this.language.getMessage('aet.home.suggestedRemediationSubtitle') : ''}
                subscriptionText={this.language.getMessage('subscriptiontext.isSubscribed')}
                classes={{
                  root: classes.studyPrepWrapper,
                  content: classes.studyPrepContent,
                  title: classes.studyPrepTitle,
                  subTitle: classes.suggestedRemediationSubTitle
                }}
                config={{
                  showSubscriptionStatus: CommonUtils.isChannelsSubscriber()
                }}
              >
                <MultiRemediationComponent
                  assets={remediationData}
                  getUserCallback={() => ({
                    userId: user.id,
                    token: user.token
                  })}
                  onInit={() => {
                    this.context.initPageTracker();
                  }}
                  channelId={getSnapshot(herobanner)?.channelId}
                  env={env.ENVIRONMENT}
                  onSeeInChannelsLinkClick={() => {
                    this.context.instructorDashboardUtils.handleSeeInChannelsLinkClick(
                      constants.AET_EVENT.LOCATION_IN_APP.SUGGESTED_REVIEW_UP_NEXT_AET
                    );
                  }}
                  containerProps={{
                    'data-componentid': constants.REMEDIATION_CONTAINER_COMPONENT_ID
                  }}
                />
              </DashboardCard>
            ) : ''}
        </LoadingContainer>
      </div>
    );
  };

  getUpNextAssignment = () => {
    const { assignment } = this.context.props;
    let sortedAssignments = assignment
      .getSortedAssignmentsByDueDate()
      .filter(a => a.dueDate && new Date(a.dueDate) >= new Date());
    sortedAssignments = getSortedAssignmentsByChapterOrSectionOrder(sortedAssignments);

    if (!sortedAssignments || sortedAssignments.length === 0) return null;
    return sortedAssignments.at(0);
  };

  /**
   * Get difficult question options
   * @returns {Object} - Difficult question options
   */
  getDifficultQuestionOptions = () => {
    const mcqStore = Framework.getStoreRegistry().getStore('mcqAssignments');
    const { mcqQuestions } = getSnapshot(mcqStore);
    const { assignment, match } = this.context.props;
    const { assignmentId, difficultTopicId } = match?.params || {};
    const statisticsData = mcqStore.getStatisticsData(difficultTopicId);
    const mostCommonIncorrectAnswerKey = CommonUtils.letterToNumber(statisticsData?.mostCommonIncorrectAnswerKey);

    const mcq = mcqQuestions.find(q => q.questionId === difficultTopicId);
    const allDifficultQuestions = assignment.getDifficultQuestionsWithStems(assignmentId);
    const selectedQuestion = allDifficultQuestions.find(q => q.questionId === difficultTopicId);

    if (!selectedQuestion) {
      return null;
    }

    return {
      questionId: selectedQuestion.questionId,
      question_stem: AssignmentHelperUtils.difficultQuestionDisplayStem(
        selectedQuestion.questionStem,
        selectedQuestion.assignmentQuestionNumber
      ),

      chapterTitle: selectedQuestion.questionTopic,
      relevant_text: mcq?.relevantText?.[0]?.quote || '',
      isFlagged: false,
      max_attempts: mcq?.maxAttempts,
      options: mcq?.options.map(option => ({
        ...option,
        correct_choice: option.correctChoice,
        is_most_common_incorrect_response: mostCommonIncorrectAnswerKey
          ? option.id === mostCommonIncorrectAnswerKey
          : false
      }))
    };
  };

  /**
   * Map modal reason values to GA `event_label`
   * @param {string[]} reasonIds
   * @returns {string|null}
   */
  mapReportReasonIdsToGaEventLabel = (reasonIds = []) => {
    const { NOT_CORRECT, NOT_RELEVANT, UNSAFE_HARMFUL, SOMETHING_ELSE } = constants.AET_EVENT.EVENT_LABEL;
    const reportTypemap = {
      'Not relevant': NOT_RELEVANT,
      'Not correct': NOT_CORRECT,
      'Unsafe/harmful else': UNSAFE_HARMFUL,
      'Something else': SOMETHING_ELSE
    };
    const resolved = (Array.isArray(reasonIds) ? reasonIds : [])
      .map((item) => {
        return reportTypemap[item] || null;
      })
      .filter(Boolean);
    if (!resolved.length) return null;
    return [...new Set(resolved)].join(',');
  };

  /**
   * Report question modal submit → Berlin feedback.
   * User-submitted fields come from ReportQuestionModal; this method adds MCQ store ids + content payload.
 * @eventNumber 868
   * @param {{ questionId: string, comments?: string, reasonForDisLike?: string[], feedback?: string, type?: string, context?: Array<{key: string, value: string}> }} formData
 * @returns {void}
   */
  submitAetMcqFeedback = (formData = {}) => {
    const {
      questionId,
      comments = '',
      reasonForDisLike = [],
      feedback,
      type,
      context: formContext
    } = formData;

    if (!questionId) {
      return;
    }

    const mcqStore = Framework.getStoreRegistry().getStore('mcqAssignments');
    const {
      mcqQuestions,
      bookMcqsRequestId: requestId,
      bookMcqsResponseId: responseId
    } = getSnapshot(mcqStore);
    const mcq = mcqQuestions.find(q => q.questionId === questionId);
    const contentPayload = {
      pageId: mcq?.pageId || '',
      questionId,
      questionStem: mcq?.questionStem || ''
    };
    const contentJson = JSON.stringify(contentPayload);
    const contentData = CommonUtils.enCodeBase64(contentJson);

    if (!requestId || !responseId) {
      return;
    }

    const book = this.context.heroDashboardPlugin.getHeroBook() || {};
    const { book_id: bookIdFromHero } = book;
    let context = [];
    // if comes from callback use it
    if (Array.isArray(formContext) && formContext.length > 0) {
      context = formContext;
    } else if (bookIdFromHero) {
      context = [{ key: 'bookId', value: bookIdFromHero }];
    }

    if (!context.length) {
      return;
    }

    const gaEventLabel = this.mapReportReasonIdsToGaEventLabel(reasonForDisLike);
    const { match, assignment: assignmentStore } = this.context.props || {};
    const { assignmentId } = match?.params || {};
    const isStudent = this.context.instructorDashboardUtils.isStudent();
    if (!isStudent && assignmentId && gaEventLabel) {
      const { ASSIGNMENT_QUESTION_DETAIL } = constants.AET_EVENT.LOCATION_IN_APP;
      const contentForQuestion = assignmentStore?.getQuestionsByAssignmentId?.(
        assignmentId,
        questionId
      );
      const chapterId = contentForQuestion?.chapterId || null;
      const difficultQuestion = assignmentStore
        ?.getAssignmentById?.(assignmentId)
        ?.difficultQuestions
        ?.find(q => q.questionId === questionId);
      // Event: 868
      CommonUtils.dispatchGaEvent(
        constants.AET_EVENT.EVENT_CATEGORY.AI_INSERTED_QUESTION,
        constants.EVENT_USER_INTERACTION,
        constants.AET_EVENT.EVENT_ACTION.SUBMIT_QUESTION_ISSUE_REPORT,
        gaEventLabel,
        {
          ...CommonUtils.aetCustomFields(this.context, ASSIGNMENT_QUESTION_DETAIL),
          assignment_id: String(assignmentId),
          question_id: String(questionId),
          chapter_id: chapterId ? String(chapterId) : null,
          count_assigned_assignments: CommonUtils.countAssignedAssignments(),
          book_topic_label: mcq?.topic || null,
          question_type: constants.AET_EVENT.QUESTION_TYPE.MCQ,
          question_attempt_number: difficultQuestion?.studentsWhoAttempted ?? null
        }
      );
    }

    const feedbackBody = {
      context,
      type,
      requestId,
      responseId,
      feedback,
      comments,
      reasonForDisLike,
      content: { data: contentData }
    };

    Framework.getEventManager().publish(AET_STORE_EVENTS.AET_MCQ_FEEDBACK_REQUESTED, { feedbackBody });
  };

  handleStrugglingStudentClick = (data = {}) => {
    const { userId } = data;
    const { match } = this.context.props;
    const { assignmentId } = match?.params || {};

    if (!userId || !assignmentId) {
      return;
    }

    const route = CommonUtils.getContextAwareRoute(
      'STUDENT_DETAILS',
      match,
      { assignmentId, studentId: userId }
    );
    Framework.redirectTo(route);
  };

  /**
   * Get difficult questions for tabular display (plain rows, no synthetic Q labels).
   * @param {Array} questions - Difficult questions
   * @returns {Array}
   */
  getDifficultQuestions = (questions) => {
    if (!questions || !Array.isArray(questions)) {
      return [];
    }

    return questions.map(question => ({ ...question }));
  };

  renderDifficultTopicDetails = () => {
    const difficultTopicsProps = {
      textInfo: {
        flaggedQuestionLabel: this.language.getMessage('aet.home.laggedQuestionLabel'),
        reviewItLabel: this.language.getMessage('aet.home.reportQuestionTitle'),
        correctAnswer: this.language.getMessage('aet.home.correctAnswer'),
        incorrectAnswer: this.language.getMessage('aet.home.incorrectAnswer'),
        behaviourTrendsLabel: this.language.getMessage('aet.home.behaviourTrendsLabel'),
        strugglingStudentsLabel: this.language.getMessage('aet.home.strugglingStudentsLabel'),
        patternsInStudensAiLabel: this.language.getMessage('aet.home.patternsInStudensAiLabel'),
        questionLabel: this.language.getMessage('aet.home.questionLabel'),
        studentAskingSimilarQuestions: this.language.getMessage('aet.home.studentAskingSimilarQuestions'),
        commentsFromStudentLabel: this.language.getMessage('aet.home.commentsFromStudentLabel'),
        disregardButtonText: this.language.getMessage('aet.home.disregard'),
        reportAsInaccurateButtonText: this.language.getMessage('aet.home.reportAsInaccurate'),
        flaggedText: this.language.getMessage('aet.home.Flagged'),
        cancelButtonLabel: this.language.getMessage('hero.removecourse.secondaryCtaText'),
        reportQuestionTitle: this.language.getMessage('aet.home.reportQuestionTitle'),
        reportQuestionLabel: this.language.getMessage('aet.home.reportQuestionLabel'),
        showComments: this.language.getMessage('aet.home.showComments'),
        studentReports: this.language.getMessage('aet.home.studentReports'),
        placeholderText: this.language.getMessage('aet.home.placeholderText')
      }
    };
    const {
      match, classes, assignment
    } = this.context.props;
    const { assignmentId, difficultTopicId } = match.params;

    // TODO: revisit this logic for data forming
    const allDifficultQuestions = assignment.getDifficultQuestionsWithStems(assignmentId);
    const selectedQuestion = allDifficultQuestions.find(q => q.questionId === difficultTopicId);
    const assignmentName = assignment.getAssignmentById(assignmentId)?.title;
    const difficultQuestions = this.getDifficultQuestionOptions();
    const rosterTotal = this.context.instructorDashboardUtils.getStudentRosterCount();
    const behaviorTrends = selectedQuestion
      ? assignment.getBehaviorTrendsFromData(selectedQuestion, rosterTotal).map(t => ({
        id: t.id,
        label: this.language.getMessage(t.labelKey),
        percentage: t.percentage,
        valueText: t.valueText,
        description: this.language.getMessage(t.description)
      }))
      : [];

    const statisticsData = this.context.props.mcqAssignments?.getStatisticsData(difficultTopicId);
    const studentsData = assignment.getStruggleStudentsWithNames(assignmentId, statisticsData?.items ?? []);
    const struggleStudents = studentsData.map((student) => ({
      ...student,
      avatarUrl: ''
    }));
    const struggleStudentList = AssignmentHelperUtils.sortStruggleStudentsByOverallDifficultyDescending(
      AssignmentHelperUtils.filterStruggleStudentsByOverallRisk(struggleStudents)
    );

    const mappedReasons = REPORT_QUESTION_REASONS.map(reason => ({
      id: reason.id,
      label: this.language.getMessage(reason.label),
      value: reason.value
    }));
    const heroBook = this.context.heroDashboardPlugin.getHeroBook() || {};
    const isActiveHeroBook = CommonUtils.isActiveBook(heroBook);
    const { book_id: reviewBookId } = heroBook;

    // create the data to be used by review modal (stats/comments only when API provides them)
    const { difficultTopicReviewData, reviewCount } = AssignmentHelperUtils.createDifficultTopicReviewData(
      difficultTopicId,
      statisticsData,
      reviewBookId
    );
    const difficultTopicReviewDataForModal = {
      ...difficultTopicReviewData,
      question: {
        ...difficultTopicReviewData.question,
        id: difficultTopicReviewData.question?.id ?? difficultTopicId
      }
    };

    return (
      <DifficultTopicDetailsContainer
        questions={difficultQuestions}
        behaviorTrends={behaviorTrends}
        mockQuestions={[]}
        students={struggleStudentList}
        assignmentName={assignmentName}
        difficultTopicReviewData={difficultTopicReviewDataForModal}
        reviewCount={reviewCount}
        onBackNavClick={() => {
          const { location } = this.context.props;
          this.context.instructorDashboardUtils.setSegmentedTabValue(location.selectedSegmentedTab);
          this.context.instructorDashboardUtils.handleBackNavClick();
        }}
        isPatternInAiUse={SHOW_PATTERN_IN_AI_USE}
        reportQuestionReasons={mappedReasons}
        onFlaggedQuestionClick={() => {
          console.log('~ FlaggedQuestion click');
        }}
        onReviewItClick={() => {
          // Event: 987
          this.handleReviewClick();
        }}
        config={{
          showBackNav: true,
          showEmptyStateCardForStrugglingStudents: !struggleStudentList?.length > 0,
          strugglingStudentsTitle: this.language.getMessage('aet.home.strugglingStudents.emptyStateTitle'),
          strugglingStudentsDescription: this.language.getMessage('aet.home.strugglingStudents.emptyStateDescription'),
          strugglingStudentsIconName: 'LightBulbIcon',
          // Struggling students table is always shown
          showStrugglingStudents: true
        }}
        open={this.context.state.isReviewPopupOpen}
        onClose={() => {
          this.handleClosePopup();
        }}
        onInaccurateClick={(data) => {
          // Event: 868
          this.submitAetMcqFeedback(data);
        }}
        classes={{
          image: classes.image,
          reviewButton: classes.reviewButton,
          questionTitle: classes.questionTitle,
          chapterHeader: classes.chapterHeader
        }}
        onStudentClick={this.handleStrugglingStudentClick}
        {...difficultTopicsProps}
        components={{
          StrugglingStudentsTableComp: this.renderStrugglingStudentsTable(),
          viewQuestions: isActiveHeroBook ? (
            <Button
              variant="contained"
              disableElevation
              className={classes.viewQuestionInAssignmentButton}
              onClick={this.handleViewQuestionInAssignmentClick}
              aria-label={this.language.getMessage('aet.home.viewQuestionInAssignment')}
            >
              {this.language.getMessage('aet.home.viewQuestionInAssignment')}
            </Button>
          ) : null
        }}
      />
    );
  };

  /**
   * Open the course e-text for this assignment, optionally at the MCQ page (same path as hero / View as student).
   */
  handleViewQuestionInAssignmentClick = () => {
    const book = this.context.heroDashboardPlugin.getHeroBook();
    const { match } = this.context.props;
    const { assignmentId, difficultTopicId } = match?.params || {};

    if (!book || !assignmentId) {
      return;
    }

    const pageId = this.context.instructorDashboardUtils.studentReviewPlugin.getPageIdOfQuestion(difficultTopicId);

    this.context.handleBookClick(
      book,
      constants.ACTIVITY_TYPE.READ,
      book.course_id,
      null,
      null,
      constants.LOCATION_IN_APP.COURSE_TAB,
      {
        ...(pageId ? { pageId } : {}),
        questionId: difficultTopicId
      }
    );
  };

  /**
   * Render struggling students table
   * @returns {React.ReactNode} - Struggling students table
   */
  renderStrugglingStudentsTable = () => {
    const {
      classes,
      match,
      assignment,
      mcqAssignments,
      questionStatisticsStatus
    } = this.context.props;

    const { assignmentId, difficultTopicId } = match?.params || {};
    const { isPending } = getSnapshot(questionStatisticsStatus);

    if (isPending) {
      return (
        <ListSkeleton length={4} />
      );
    }

    const currentAssignment = assignment.getAssignmentById(assignmentId);

    if (!currentAssignment) return null;

    const statisticsData = mcqAssignments?.getStatisticsData(difficultTopicId);
    const studentsData = assignment.getStruggleStudentsWithNames(
      currentAssignment?.assignmentId,
      statisticsData?.items ?? []
    );

    if (studentsData.length === 0) {
      return (
        <EmptyStateCard
          title={this.language.getMessage('aet.assignmentStudentDetails.noDataFound')}
          description={this.language.getMessage('aet.assignmentStudentDetails.startAssignmentDescription')}
          iconName="LightBulbIcon"
        />
      );
    }

    const filtered = AssignmentHelperUtils.filterStruggleStudentsByOverallRisk(studentsData);
    const tableRows = AssignmentHelperUtils.sortStruggleStudentsByOverallDifficultyDescending(filtered);

    if (tableRows.length === 0) {
      return (
        <EmptyStateCard
          title={this.language.getMessage('aet.assignmentStudentDetails.noDataFound')}
          description={this.language.getMessage('aet.assignmentStudentDetails.startAssignmentDescription')}
          iconName="LightBulbIcon"
        />
      );
    }

    const { ASSIGNMENT_QUESTION_DETAIL } = constants.AET_EVENT.LOCATION_IN_APP;
    const { UP_NEXT_INSIGHTS_TAB } = constants.AET_EVENT.INITIATE_POINT;

    return (
      // Event: 891
      <div
        className={classes.strugglingStudentsTabularContainer}
        style={{ marginTop: '16px' }}
        data-componentid={constants.STRUGGLING_STUDENTS_CONTAINER_COMPONENT_ID}
        data-assignment-id={String(currentAssignment.assignmentId)}
        data-location-in-app={ASSIGNMENT_QUESTION_DETAIL}
      >
        <DashboardCard
          title={this.language.getMessage('aet.home.strugglingStudentsHereLabel')}
          classes={{
            root: classes.questionRoot,
            title: classes.title
          }}
          onInit={() => {
            this.context.initDasboardPageTracker();
          }}
        >
          <TabularContainer
            config={{
              showSearch: false,
              showColumnDivider: false,
              searchPlaceholder: null
            }}
            data={tableRows}
            columns={[
              {
                title: this.language.getMessage('aet.studentRoster.title'),
                primarySearch: TABULAR_SEARCH_SORT.SEARCH,
                primarySearchFields: ['name'],
                render: data => (
                  <OverviewTemplate
                    classes={{
                      subtitle2: classes.subtitle2,
                      image: classes.image
                    }}
                    name={`${data.name}`}
                    imageSrc={data.avatarUrl || ''}
                    initials={AssignmentHelperUtils.getInitials(data.name)}
                  />
                ),
                sort: ['name', 'string']
              },
              {
                title: this.language.getMessage('aet.home.StudentDetails.comprehension'),
                isSortable: false,
                render: (data) => {
                  const comprehension = data?.answerConfidence;

                  return (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <BadgeTemplate
                        topics={null}
                        status={AssignmentHelperUtils.normalizeStatus(comprehension || constants.PERFORMANCE_STATUS.NA)}
                        label={AssignmentHelperUtils.getSectionNameFromStatus(comprehension)}
                        classes={{ chip: classes.chip, dotIconContainer: classes.dotIconContainer }}
                      />
                    </div>
                  );
                }
              }
            ]}
            onRowClick={(student) => {
              // Event: 922
              this.context.instructorDashboardUtils.assignmentPlugin.dispatchOpenSpecificStudentInsightsEvent(
                ASSIGNMENT_QUESTION_DETAIL,
                UP_NEXT_INSIGHTS_TAB,
                currentAssignment.assignmentId,
                student.userId
              );
              Framework.redirectTo(
                CommonUtils.getContextAwareRoute(
                  'STUDENT_DETAILS', match, { assignmentId: currentAssignment.assignmentId, studentId: student.userId }
                )
              );
            }}
          />
        </DashboardCard>
      </div>
    );
  };

  /**
   * Triggers the Open Specific Question Insights event
   * @eventNumber 921
   * @param {Object} row - Tabular row with questionId
   * @param {Object} match - react-router match
   * @param {Object} currentAssignment - Current assignment model
   * @param {boolean} showOnlyDifficult - "Show only Difficult questions" toggle
   * @param {Array<Object>} difficultQuestions - Difficult questions for this assignment
   * @returns {void}
   */
  dispatchOpenSpecificQuestionInsightsEvent = (row, match, currentAssignment, showOnlyDifficult, difficultQuestions) => {
    if (this.context.instructorDashboardUtils.isStudent()) {
      return;
    }
    const { assignmentId, studentId } = match?.params || {};
    const currentTabId = this.context.instructorDashboardUtils.getCurrentTab();
    const { ASSIGNMENTS_DUE_NEXT, ASSIGNMENT_DETAILS } = constants.AET_EVENT.LOCATION_IN_APP;
    const { ALL_STUDENTS, SINGLE_STUDENT } = constants.AET_EVENT.EVENT_LABEL;
    const { UP_NEXT_INSIGHTS_TAB, ASSIGNMENTS_INSIGHTS_TAB } = constants.AET_EVENT.INITIATE_POINT;
    const { ASSIGNMENTS, UP_NEXT } = constants.INSTRUCTOR_DASHBOARD_TABS;

    let locationInApp = null;
    let initiatePoint = null;
    let eventLabel = null;
    let studentIdField = null;

    if (!assignmentId && currentTabId === UP_NEXT) {
      locationInApp = ASSIGNMENTS_DUE_NEXT;
      initiatePoint = UP_NEXT_INSIGHTS_TAB;
      eventLabel = ALL_STUDENTS;
      studentIdField = null;
    } else if (assignmentId && currentTabId === ASSIGNMENTS) {
      locationInApp = ASSIGNMENT_DETAILS;
      initiatePoint = ASSIGNMENTS_INSIGHTS_TAB;
      eventLabel = SINGLE_STUDENT;
      studentIdField = studentId || null;
    }

    if (!locationInApp || !row?.questionId) {
      return;
    }

    const difficultQuestionList = Array.isArray(difficultQuestions)
      ? AssignmentHelperUtils.filterDifficultQuestionsByOverallRisk(difficultQuestions)
      : [];
    const isDifficultQuestion = difficultQuestionList.some(
      (q) => String(q.questionId) === String(row.questionId)
    );

    const customFields = {
      ...CommonUtils.aetCustomFields(this.context, locationInApp),
      assignment_id: String(currentAssignment.assignmentId),
      question_id: row.questionId,
      initiate_point: initiatePoint,
      is_difficult_question: isDifficultQuestion,
      student_id: studentIdField
    };

    // Event: 921
    CommonUtils.dispatchGaEvent(
      constants.AET_EVENT.EVENT_CATEGORY.DASHBOARD,
      constants.EVENT_USER_INTERACTION,
      constants.AET_EVENT.EVENT_ACTION.OPEN_SPECIFIC_QUESTION_INSIGHTS,
      eventLabel,
      customFields
    );
  };

  renderDifficultQuestions = ({ showHeader = true, limit = null, showEmptyState = false }) => {
    if (!this.context.instructorDashboardUtils.showDifficultyAndStruggleInsights()) {
      return null;
    }

    const {
      classes, match, difficultQuestionsStatus, assignment, courseAssignmentProgressStatus
    } = this.context.props;
    const { assignmentId } = match?.params || {};
    const { isPending } = getSnapshot(difficultQuestionsStatus);

    if (isPending || (showEmptyState && courseAssignmentProgressStatus
      && getSnapshot(courseAssignmentProgressStatus)?.isPending)) {
      return (
        <ListSkeleton length={4} />
      );
    }
    const currentAssignment = assignmentId
      ? assignment.getAssignmentById(assignmentId)
      : this.getUpNextAssignment();
    if (!currentAssignment) return null;

    if (
      assignmentId
      && showEmptyState
      && !this.context.instructorDashboardUtils.hasAssignmentInsightsActivity(assignmentId)
    ) {
      return this.context.instructorDashboardUtils.renderAssignmentNotStartedEmptyState();
    }

    const difficultTopicsRaw = assignment.getDifficultQuestionsWithStems(currentAssignment.assignmentId);
    const showOnlyDifficult = Boolean(this.context.state.showOnlyDifficultQuestions);
    const isUpNextHome = !assignmentId;
    // Up Next + toggle on: filter by performanceRisk; Assignment L2 toggle off: full store list, no filter
    const rawRowList = isUpNextHome || showOnlyDifficult
      ? AssignmentHelperUtils.filterDifficultQuestionsByOverallRisk(difficultTopicsRaw)
      : difficultTopicsRaw;


    const sliced = limit && Array.isArray(rawRowList)
      ? rawRowList.slice(0, limit)
      : rawRowList;
    const slicedWithQuestionNumbers = AssignmentHelperUtils.addAssignmentQuestionNumbersToDifficultQuestions(
      currentAssignment,
      sliced
    );
    const difficultTopics = this.getDifficultQuestions(slicedWithQuestionNumbers);

    if (!difficultTopics || difficultTopics.length === 0) {
      // Don't show empty state if user in upnext view
      if (!showEmptyState) {
        return null;
      }

      // Toggle on + students started but no difficult questions match the filter → "No data"
      return this.context.instructorDashboardUtils.renderAssignmentNotStartedEmptyState(
        Boolean(
          assignmentId
          && showOnlyDifficult
          && this.context.instructorDashboardUtils.hasAssignmentInsightsActivity(assignmentId)
        )
      );
    }

    const showTabularSearch = Boolean(assignmentId); // dont show search in UpNExt
    const showSearchEffective = showTabularSearch && TABULAR_SEARCH_SORT.SEARCH;
    const isUpNextTab = this.context.instructorDashboardUtils.getCurrentTab()
      === constants.INSTRUCTOR_DASHBOARD_TABS.UP_NEXT;
    const { UP_NEXT_AET, ASSIGNMENTS_PAGE } = constants.AET_EVENT.LOCATION_IN_APP;
    const difficultQuestionsViewportLocation = isUpNextTab
      ? UP_NEXT_AET
      : (assignmentId ? ASSIGNMENTS_PAGE : constants.LOCATION_IN_APP.AUTHHOME);

    return (
      // Event: 886
      <div
        className={classes.difficultQuestionsTabularContainer}
        data-componentid={constants.DIFFICULT_QUESTIONS_CONTAINER_COMPONENT_ID}
        data-assignment-id={String(currentAssignment.assignmentId)}
        data-location-in-app={difficultQuestionsViewportLocation}
      >
        <DashboardCard
          title={this.language.getMessage('aet.home.assignmentDifficultQuestions')}
          classes={{
            root: classes.questionRoot,
            title: classes.title
          }}
          config={{
            showHeader
          }}
          onInit={() => {
            this.context.initDasboardPageTracker();
          }}
        >
          <TabularContainer
            config={{
              showSearch: showSearchEffective,
              showColumnDivider: false,
              ...(showSearchEffective
                ? { searchPlaceholder: this.language.getMessage('aet.tabular.searchPlaceholder.questions') }
                : {})
            }}
            data={difficultTopics}
            columns={[
              {
                title: this.language.getMessage('aet.home.StudentDetails.questions'),
                primarySearch: TABULAR_SEARCH_SORT.SEARCH,
                primarySearchFields: ['questionStem', 'questionTopic'],
                isSortable: false,
                render: data => (
                  <div className={classes.questionStem}>
                    <OverviewTemplate
                      name={AssignmentHelperUtils.difficultQuestionDisplayStem(
                        data.questionStem,
                        data.assignmentQuestionNumber
                      )}
                      subtitle={data.questionTopic}
                      classes={{
                        primaryCol1: classes.primaryCol1
                      }}
                    />
                  </div>
                )
              },
              {
                title: this.language.getMessage('aet.home.behaviorTrends'),
                isSortable: false,
                render: (data) => {
                  const behaviorTrends = this.context.instructorDashboardUtils
                    .getBehaviorTrendsForQuestion(
                      currentAssignment?.assignmentId,
                      data.questionId,
                      data,
                      { removeLowEngagementWhenMultiple: true, useEngagementNaLabel: true }
                    );
                  return (
                    <div className={classes.badgeTemplateContainer}>
                      <BadgeTemplate
                        topics={behaviorTrends}
                        status={TOPIC_STYLES}
                        classes={{ chip: this.isNaInsightBadge(behaviorTrends)
                          ? classes.insightNaChip
                          : classes.chip
                        }}
                      />
                    </div>
                  );
                }
              }
            ]}
            onRowClick={(row) => {
              // Event: 921
              this.dispatchOpenSpecificQuestionInsightsEvent(
                row,
                match,
                currentAssignment,
                showOnlyDifficult,
                difficultTopicsRaw
              );
              this.context.instructorDashboardUtils.handlePageRedirection(
                CommonUtils.getContextAwareRoute('DIFFICULT_TOPIC_DETAILS', match, {
                  assignmentId: currentAssignment.assignmentId,
                  difficultTopicId: row.questionId
                })
              );
            }}
          />
        </DashboardCard>
      </div>
    );
  };

  renderStrugglingStudents = () => {
    if (!this.context.instructorDashboardUtils.showDifficultyAndStruggleInsights()) {
      return null;
    }

    const {
      classes, match, assignment, struggleStudentsStatus
    } = this.context.props;
    const { assignmentId } = match?.params || {};
    const { isPending } = getSnapshot(struggleStudentsStatus);

    if (isPending) {
      return (
        <ListSkeleton length={4} />
      );
    }
    const currentAssignment = assignmentId
      ? assignment.getAssignmentById(assignmentId)
      : this.getUpNextAssignment();

    if (!currentAssignment) return null;
    const studentsData = assignment.getStruggleStudentsWithNames(currentAssignment?.assignmentId);
    if (studentsData.length === 0) {
      return null;
    }

    const showOnlyStruggling = Boolean(this.context.state.showOnlyStrugglingStudents);
    const isUpNextHome = !assignmentId;
    const strictStrugglingInsightMode = isUpNextHome || showOnlyStruggling;
    let tableRows;
    // in upnext or toggle on state, we must sort students by risk, or else sort by aphabetical order
    if (strictStrugglingInsightMode) {
      const filtered = AssignmentHelperUtils.filterStruggleStudentsByOverallRisk(studentsData);
      tableRows = AssignmentHelperUtils.sortStruggleStudentsByOverallDifficultyDescending(filtered);
    } else {
      tableRows = this.context.instructorDashboardUtils.sortNameAlphabetically(studentsData);
    }

    if (tableRows.length === 0) {
      return null;
    }


    const showTabularSearch = Boolean(assignmentId); // dont show search in UpNExt
    const showSearchEffective = showTabularSearch && TABULAR_SEARCH_SORT.SEARCH;
    const { ASSIGNMENTS_DUE_NEXT, UP_NEXT_AET } = constants.AET_EVENT.LOCATION_IN_APP;
    const { UP_NEXT_INSIGHTS_TAB } = constants.AET_EVENT.INITIATE_POINT;

    return (
      // Event: 891
      <div
        className={classes.strugglingStudentsTabularContainer}
        data-componentid={constants.STRUGGLING_STUDENTS_CONTAINER_COMPONENT_ID}
        data-assignment-id={String(currentAssignment.assignmentId)}
        data-location-in-app={UP_NEXT_AET}
      >
        <DashboardCard
          title={this.language.getMessage('aet.home.strugglingStudentsLabel')}
          classes={{
            root: classes.questionRoot,
            title: classes.title
          }}
          onInit={() => {
            this.context.initDasboardPageTracker();
          }}
        >
          <TabularContainer
            config={{
              showSearch: showSearchEffective,
              showColumnDivider: false,
              ...(showSearchEffective
                ? { searchPlaceholder: this.language.getMessage('aet.tabular.searchPlaceholder.students') }
                : {})
            }}
            data={tableRows}
            columns={[
              {
                title: this.language.getMessage('aet.studentRoster.title'),
                primarySearch: TABULAR_SEARCH_SORT.SEARCH,
                primarySearchFields: ['name'],
                isSortable: false,
                render: data => (
                  <OverviewTemplate
                    classes={{
                      subtitle2: classes.subtitle2,
                      image: classes.image
                    }}
                    name={`${data.name}`}
                    imageSrc={data.avatarUrl || ''}
                    initials={AssignmentHelperUtils.getInitials(data.name)}
                  />
                ),
                sort: ['name', 'string']
              },
              {
                title: this.language.getMessage('aet.home.behaviors'),
                isSortable: false,
                render: (data) => {
                  const riskFactors = this.context.instructorDashboardUtils
                    .getRiskFactorsForStudent(
                      currentAssignment?.assignmentId,
                      data.userId,
                      data,
                      { removeLowEngagementWhenMultiple: strictStrugglingInsightMode }
                    );
                  return (
                    <div className={classes.badgeTemplateContainer}>
                      <BadgeTemplate
                        topics={riskFactors}
                        status={TOPIC_STYLES}
                        classes={{ chip: this.isNaInsightBadge(riskFactors)
                          ? classes.insightNaChip
                          : classes.behaviorsTextColor
                        }}
                      />
                    </div>
                  );
                }
              }
            ]}
            onRowClick={(student) => {
              // Event: 922
              this.context.instructorDashboardUtils.assignmentPlugin.dispatchOpenSpecificStudentInsightsEvent(
                ASSIGNMENTS_DUE_NEXT,
                UP_NEXT_INSIGHTS_TAB,
                currentAssignment.assignmentId,
                student.userId
              );
              Framework.redirectTo(
                CommonUtils.getContextAwareRoute('STUDENT_DETAILS', match, { assignmentId: currentAssignment.assignmentId, studentId: student.userId })
              );
            }}
          />
        </DashboardCard>
      </div>
    );
  };

  renderUpNextAssignment = (upNextAssignment) => {
    const { classes } = this.context.props;
    const { courseAssignmentProgressStatus } = this.context.props;
    const { isPending, isError } = getSnapshot(courseAssignmentProgressStatus);

    return (
      <>
        <DashboardCard
          title={upNextAssignment.title}
          actions={this.getUpNextCardActions(upNextAssignment)}
          classes={{
            root: classes.upNextAssignmentRoot,
            content: classes.upNextAssignmentContent
          }}
          onInit={() => {
            this.context.initPageTracker();
            // Event: 877
            this.context.instructorDashboardUtils.tabChangeEvent();
            // Event: 876
            this.context.instructorDashboardUtils.tiggerSystemNextAssignmentEvent();
          }}
        >
          <div className={classes.upNextDetails}>
            {this.renderProgressCard({
              progress: upNextAssignment?.progress || null,
              isLoading: isPending,
              isError
            })}
            {this.renderCustomCardList(CommonUtils.transformToAssignmentTags(upNextAssignment?.assignmentTags))}
          </div>
          {this.renderDifficultQuestions({ showHeader: true, limit: null })}
          {this.renderStrugglingStudents()}
        </DashboardCard>
      </>
    );
  };

  getCurrentAssignment = () => {
    const { editingUpNextAssignment } = this.context.state;
    return editingUpNextAssignment;
  };

  getUpNextCardActions = (upNextAssignment) => {
    const editDeleteActions = this.context.instructorDashboardUtils.getAssignmentEditDeleteActions(
      upNextAssignment,
      () => {
        this.context.setState({
          isAddAssignmentClicked: true,
          isUpNextEditClicked: true,
          editingUpNextAssignment: upNextAssignment
        });
        // Event: 881
        this.context.instructorDashboardUtils.triggerIndividualAssignmentEditEvent(
          constants.LOCATION_IN_APP.UP_NEXT_TAB
        );
      },
      () => {
        const assignmentId = upNextAssignment?.assignmentId;
        if (assignmentId) {
          this.context.instructorDashboardUtils.openDeleteConfirmation([assignmentId]);
        }
      }
    );
    const heroBook = this.context.heroDashboardPlugin.getHeroBook() || {};
    const isActiveHeroBook = CommonUtils.isActiveBook(heroBook);

    return {
      left: [
        ...editDeleteActions
      ],
      right: [
        ...(isActiveHeroBook
          ? [{
            type: 'button',
            text: this.language.getMessage('aet.home.viewAsStudentButton'),
            iconName: 'OpenBookIcon',
            callback: () => {
              if (upNextAssignment?.assignmentId) {
                this.context.heroDashboardPlugin.handleCtaClick(false, upNextAssignment.assignmentId);
                // Event: 884
                this.context.instructorDashboardUtils.tiggerViewAsStudentEvent(
                  upNextAssignment.assignmentId,
                  constants.AET_EVENT.LOCATION_IN_APP.UP_NEXT_AET
                );
              }
            }
          }]
          : []),
        {
          type: 'button',
          text: this.language.getMessage('aet.home.viewDetailsButton'),
          iconName: 'ViewDetailsIcon',
          // Event: 894
          callback: this.handleViewDetails,
          details: {
            assignmentId: upNextAssignment?.assignmentId
          }
        }
      ]
    };
  };

  renderProgressCard = (args) => {
    const {
      isLoading,
      isError
    } = args;
    let { progress } = args;

    if (isLoading) {
      return (
        <CardSkeleton />
      );
    }

    if (isError || !progress) {
      progress = CommonUtils.progressCardData();
    }


    const {
      dueDate,
      completedOnTimePercentage,
      completedLatePercentage,
      inProgressPercentage,
      notStartedPercentage,
      completedOnTimeCount,
      completedLateCount,
      inProgressCount,
      notStartedCount
    } = progress;

    const completedOnTimeColor = AssignmentHelperUtils.getProgressCardColor(constants.STATUS_COLOR_MAP.COMPLETED);
    const completedLateColor = AssignmentHelperUtils.getProgressCardColor(constants.STATUS_COLOR_MAP.COMPLETED_PAST_DUE);
    const inProgressColor = AssignmentHelperUtils.getProgressCardColor(constants.STATUS_COLOR_MAP.IN_PROGRESS);
    const notStartedColor = AssignmentHelperUtils.getProgressCardColor(constants.STATUS_COLOR_MAP.NOT_STARTED);
    const localTimeZoneLabel = this.context.instructorDashboardUtils.getLocalTimeZoneLabel();

    return (
      <ProgressCard
        title={this.language.getMessage('aet.assignments.completionTitle')}
        segments={[
          {
            label: this.language.getMessage('aet.home.progressCompletedOnTimeLabel'),
            value: completedOnTimeCount,
            valueInPercentage: completedOnTimePercentage,
            color: completedOnTimeColor,
            type: constants.STATUS_COLOR_MAP.COMPLETED
          },
          {
            label: this.language.getMessage('aet.home.progressCompletedPastDueLabel'),
            value: completedLateCount,
            valueInPercentage: completedLatePercentage,
            color: completedLateColor
          },
          {
            label: this.language.getMessage('aet.home.progressInProgressLabel'),
            value: inProgressCount,
            valueInPercentage: inProgressPercentage,
            color: inProgressColor
          },
          {
            label: this.language.getMessage('aet.home.NotAttemptedLabel'),
            value: notStartedCount,
            valueInPercentage: notStartedPercentage,
            color: notStartedColor
          }
        ]}
        footerContent={[{
          label: this.language.getMessage('aet.home.due'),
          value: localTimeZoneLabel
            ? `${CommonUtils.formatLocalDateTime(dueDate)} ${localTimeZoneLabel}`
            : CommonUtils.formatLocalDateTime(dueDate)
        }]}
        progressLabel={completedOnTimeCount}
        progressSubLabel={this.language.getMessage('aet.home.progressCompletedOnTimeLabel')}
        enableDotIndicators={false}
      />
    );
  };

  renderCustomCardList = chapters => (
    <CustomCardList
      items={chapters}
      title={this.language.getMessage('aet.home.assignmentIncludesTitle')}
    />
  );

  /**
   * Get the default instructor welcome data
   * @returns {Object}
   */
  getDefaultInstructorWelcomeData = () => ({
    title: this.language.getMessage('aet.welcome.welcome.Title'),
    sectionTitle: this.language.getMessage('aet.welcome.title'),
    instructions: [
      {
        title: this.language.getMessage('aet.welcome.createAssignmentsTitle'),
        description: this.language.getMessage('aet.welcome.createAssignmentsDescription')
      },
      {
        title: this.language.getMessage('aet.welcome.LMSAccounTitle'),
        description: this.language.getMessage('aet.welcome.LMSAccounDescription')
      },
      {
        title: this.language.getMessage('aet.welcome.progressViewTitle'),
        description: this.language.getMessage('aet.welcome.progressViewDescription')
      }
    ],
    buttonText: this.language.getMessage('aet.home.welcomeButtonText')
  });

  /**
   * Get the welcome copy for the welcome card
   * @returns {Object}
   */
  getWelcomeCopy = () => {
    const heroBook = this.context.heroDashboardPlugin.getHeroBook() || {};
    const { isLms } = heroBook || {};

    if (isLms) {
      return this.context.getContainerData(
        'heroView', 'instructorWelcome', this.getDefaultInstructorWelcomeData()
      );
    }

    return {
      ...this.context.getContainerData(
        'heroView', 'instructorWelcomeNonLMS'
      ),
      buttonText: this.context.getContainerData(
        'heroView', 'instructorWelcome'
      )?.buttonText || this.language.getMessage('aet.home.welcomeButtonText')
    };
  };

  // Render the welcome card
  renderWelcomeCard = () => {
    const { classes } = this.context.props;
    const instructorWelcomeData = this.getWelcomeCopy();
    const {
      title,
      sectionTitle,
      instructions,
      buttonText
    } = instructorWelcomeData || {};

    return (
      <DashboardCard
        title={title}
        classes={{
          root: classes.upNextAssignmentRoot
        }}
      >
        <WelcomeCard
          className={classes.welcomeCard}
          content={{
            sectionTitle,
            instructions
          }}
          buttonCta={{
            text: buttonText,
            icon: null
          }}
          showButton
          onCtaClick={() => {
            const { match } = this.context.props;
            const route = CommonUtils.getContextAwareRoute('ASSIGNMENTS', match);
            const [pathname, search] = route.split('?');
            Framework.redirectTo({
              pathname,
              search: search ? `?${search}` : null,
              state: {
                from: constants.LOCATION_IN_APP.AUTHHOME
              }
            });
          }}
          showBanner={false}
          onInit={() => {
          // Event: 900
            CommonUtils.dispatchGaEvent(
              constants.AET_EVENT.EVENT_CATEGORY.DASHBOARD,
              constants.EVENT_USER_INTERACTION,
              constants.AET_EVENT.EVENT_ACTION.VIEW_ASSIGNMENTS_EMPTY_STATE,
              null,
              CommonUtils.aetCustomFields(this.context, constants.LOCATION_IN_APP.AUTHHOME)
            );
          }}
        />
      </DashboardCard>
    );
  };
}
